import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { Badge } from "../Badge";
import { CalendarDays, CalendarClock, ArrowRight, MapPin } from "lucide-react";

export const formatTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
};

export const formatDate = (d) => {
  if (!d) return "";
  return new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};

export default function CaregiverDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/caregiver/overview").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null) return <p className="text-slate-500 font-semibold">Loading your schedule…</p>;
  if (data === false) return <p className="text-red-700 font-semibold">Unable to load your dashboard. Please try again.</p>;

  const next = data.next_visit;

  return (
    <div data-testid="caregiver-dashboard">
      <h1 className="font-display text-3xl md:text-4xl text-brand-navy">Hello, {data.caregiver?.name?.split(" ")[0] || "Caregiver"}</h1>
      <p className="mt-2 text-[15px] text-slate-600">Here is your schedule at a glance.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 max-w-md">
        <div data-testid="cg-today-count" className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <CalendarDays className="h-5 w-5 text-brand-teal" strokeWidth={1.6} />
            <span className="font-display text-3xl font-semibold text-brand-navy">{data.today.length}</span>
          </div>
          <p className="mt-2 text-sm font-bold text-slate-600">Today's Visits</p>
        </div>
        <div data-testid="cg-week-count" className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <CalendarClock className="h-5 w-5 text-brand-teal" strokeWidth={1.6} />
            <span className="font-display text-3xl font-semibold text-brand-navy">{data.week_count}</span>
          </div>
          <p className="mt-2 text-sm font-bold text-slate-600">Visits This Week</p>
        </div>
      </div>

      <section className="mt-8">
        <h2 className="font-display text-2xl text-brand-navy">Your Next Visit</h2>
        {next ? (
          <div data-testid="next-visit-card" className="mt-4 rounded-2xl bg-brand-navy text-white p-6 md:p-8 shadow-[0_16px_40px_rgb(11,27,61,0.25)]">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-cyan">Next Upcoming Visit</p>
                <p className="mt-2 font-display text-2xl md:text-3xl">{next.patient_name}</p>
              </div>
              <Badge kind="visit" value={next.status} />
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[15px] text-slate-200">
              <p><strong className="text-white">Date:</strong> {formatDate(next.date)}</p>
              <p><strong className="text-white">Time:</strong> {formatTime(next.start_time)} – {formatTime(next.end_time)}</p>
              <p><strong className="text-white">Type of Care:</strong> {next.care_type}</p>
              <p className="flex items-start gap-1.5"><MapPin className="h-4 w-4 mt-1 shrink-0 text-brand-cyan" /> {next.patient_address || "Address on file"}</p>
            </div>
            <Link
              to={`/caregiver/visits/${next.id}`}
              data-testid="next-visit-details"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-teal px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-cyan hover:text-brand-navy transition-colors duration-300 active:scale-95"
            >
              View Visit Details <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">No upcoming visits assigned. Enjoy your day!</div>
        )}
      </section>

      <section className="mt-8">
        <h2 className="font-display text-2xl text-brand-navy">Upcoming — Next 7 Days</h2>
        <ul className="mt-4 space-y-3">
          {data.upcoming_7_days.length === 0 && <li className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-500">No visits scheduled in the next 7 days.</li>}
          {data.upcoming_7_days.map((v) => (
            <li key={v.id}>
              <Link to={`/caregiver/visits/${v.id}`} data-testid={`cg-upcoming-${v.id}`} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:border-brand-teal/40 transition-colors duration-300">
                <div className="min-w-0">
                  <p className="font-bold text-brand-navy truncate">{v.patient_name} · {v.care_type}</p>
                  <p className="text-sm text-slate-500">{formatDate(v.date)} · {formatTime(v.start_time)} – {formatTime(v.end_time)}</p>
                </div>
                <Badge kind="visit" value={v.status} />
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
